import { useState, useEffect } from "react"
import { makeVar, useReactiveVar } from "@apollo/client"
import { useAuth0 } from "@auth0/auth0-react"
import { useLocation } from "@reach/router"

import { useIsUserUsingPasswordLoginQuery } from "~graphql/generated/graphql"
import {
    trackLogin,
    trackLogout,
} from "~utils/event-tracking/event-tracking-helpers"
import usePersistedGuestAccessToken from "~utils/use-persisted-guest-access-token"

// With auth disabled, auth0 will still be active and possibly log in the user.
// However, the useAuth hook will always return null for the authUser and isAuthenticated: false,
// which will make the app behave as if you are not logged in. Guest user tokens are still active.
const DISABLED_AUTH0_ROUTES = ["/widget-content"]

export const accessTokenVar = makeVar<string | null>(null)

export function useAuth() {
    const [error, setError] = useState<Error>()
    const {
        isAuthenticated: hasBeenAuthenticated,
        isLoading: isLoadingAuth0,
        getAccessTokenSilently,
        loginWithRedirect,
        logout,
        user: authUser,
    } = useAuth0()

    const accessToken = useReactiveVar(accessTokenVar)
    const { guestAccessToken } = usePersistedGuestAccessToken()

    // Load the guest access token from local storage if not logged in.
    useEffect(() => {
        if (hasBeenAuthenticated || isLoadingAuth0 || accessToken) return

        accessTokenVar(guestAccessToken)
    }, [hasBeenAuthenticated, guestAccessToken, accessToken, isLoadingAuth0])

    useEffect(() => {
        if (!hasBeenAuthenticated || Boolean(accessToken)) return

        void getAndSetAccessToken({ getAccessTokenSilently, setError })
    }, [hasBeenAuthenticated, getAccessTokenSilently])

    const isAuthenticated = hasBeenAuthenticated && Boolean(accessToken)
    const isLoadingToken = !isAuthenticated && hasBeenAuthenticated
    const isLoading = isLoadingToken || isLoadingAuth0
    const isAuthDisabled = useIsAuthDisabled()

    const wrappedLogin = ({
        isSignup,
        returnTo = window.location.pathname + window.location.search,
    }: { isSignup?: boolean; returnTo?: string } = {}) => {
        trackLogin(authUser?.sub)
        return loginWithRedirect({
            appState: { returnTo },
            authorizationParams: { screen_hint: isSignup ? "signup" : "login" },
        })
    }

    const wrappedLogout = async () => {
        await logout({ logoutParams: { returnTo: window.location.origin } })
        accessTokenVar(null)
        trackLogout()
    }

    return {
        // Only allow guest access token if auth is disabled.
        accessToken: isAuthDisabled ? guestAccessToken : accessToken,
        authUser: isAuthDisabled ? null : authUser,
        isLoading,
        error,
        isAuthenticated: isAuthDisabled ? false : isAuthenticated,
        hasBeenAuthenticated,
        loginWithRedirect: wrappedLogin,
        logout: wrappedLogout,
    }
}

export function useIsUserUsingPasswordLogin() {
    const { data } = useIsUserUsingPasswordLoginQuery()

    return data?.isUserUsingPasswordLogin ?? false
}

function useIsAuthDisabled() {
    const { pathname } = useLocation()

    return DISABLED_AUTH0_ROUTES.includes(pathname)
}

async function getAndSetAccessToken({
    getAccessTokenSilently,
    setError,
}: {
    getAccessTokenSilently: () => Promise<string>
    setError: (error: Error) => void
}) {
    try {
        const accessToken = await getAccessTokenSilently()

        accessTokenVar(accessToken)
    } catch (error) {
        if (error instanceof Error) {
            setError(error)
        } else {
            setError(new Error("An unknown error occurred"))
        }
    }
}
